import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { BlogPost, categoryColors } from '@/data/blogPosts';

interface BlogPostCardProps {
  post: BlogPost;
  featured?: boolean;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ post, featured = false }) => {
  const colors = categoryColors[post.category];

  if (featured) {
    return (
      <article className="group relative overflow-hidden rounded-2xl bg-sidebar border border-border transition-all duration-500 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1">
        <div className="p-8 md:p-10">
          {/* Category pill */}
          <div className="mb-6">
            <span className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase ${colors.bg} ${colors.text} border ${colors.border}`}>
              {post.category}
            </span>
          </div>

          {/* Title */}
          <Link to={`/blog/${post.slug}`} className="block mb-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight group-hover:text-primary transition-colors duration-300">
              {post.title}
            </h2>
          </Link>

          {/* Excerpt */}
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6 line-clamp-3">
            {post.excerpt}
          </p>

          {/* Meta row */}
          <div className="flex items-center justify-between pt-6 border-t border-border">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-semibold text-primary">
                    {post.author.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <span className="font-medium text-foreground">{post.author}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </time>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{post.readingTime} min read</span>
              </div>
            </div>

            <Link
              to={`/blog/${post.slug}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all duration-300"
            >
              Read article
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Decorative gradient corner */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/5 to-transparent pointer-events-none" />
      </article>
    );
  }

  return (
    <article className="group relative flex flex-col h-full overflow-hidden rounded-xl bg-sidebar border border-border transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 hover:border-primary/30">
      <div className="flex flex-col flex-1 p-6">
        {/* Category pill */}
        <div className="mb-4">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide uppercase ${colors.bg} ${colors.text} border ${colors.border}`}>
            {post.category}
          </span>
        </div>

        {/* Title */}
        <Link to={`/blog/${post.slug}`} className="block mb-3">
          <h3 className="text-lg font-bold text-foreground leading-snug group-hover:text-primary transition-colors duration-300 line-clamp-2">
            {post.title}
          </h3>
        </Link>

        {/* Excerpt */}
        <p className="text-muted-foreground text-sm leading-relaxed mb-5 flex-1 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Meta row */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}
              </time>
            </div>
            <span className="text-border">|</span>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              <span>{post.readingTime} min</span>
            </div>
          </div>

          <Link
            to={`/blog/${post.slug}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-all duration-300"
          >
            Read
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogPostCard;
